import React, { useCallback, useEffect } from "react";
import { ThemeName } from "@/style/themes";
import {
  PlayerGameOutHttp,
  RedeemPrepaidCodeOutHttp,
  StartPrepaidCheckoutOutHttp,
} from "@riot-private/riotpay-sdk-pmc";
import { useCashCode } from "../hooks/useCashCode";
import { GameInfo, useGamesPlayed } from "../hooks/useGamesPlayed";
import siteConfig from "@/variables";
import { useSessionStorage } from "react-use";
import { configureRiotPaySDK, ServiceName } from "@riot-private/riotpay-sdk-common";
import { isPrepaidCode } from "@riot-private/riotpay-sdk";
import { BuyerInfoData } from "../components/BuyerInfoDetails";

// Define cash code state interface
type CashCodeState = {
  isCashCodeActive: (providedCode?: string) => boolean;
  theme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  code?: string;
  setCode: (code: string) => void;
  cancelRedeem: () => void;
  validationDetails?: StartPrepaidCheckoutOutHttp;
  redemptionDetails?: RedeemPrepaidCodeOutHttp;
  validateCode: (
    game: string,
    rsoToken: string,
    language: string,
    country?: string,
    gameShard?: string,
    buyerInfo?: BuyerInfoData,
  ) => Promise<StartPrepaidCheckoutOutHttp>;
  redeemCode: () => Promise<RedeemPrepaidCodeOutHttp>;
  getGameInfo: (theme: ThemeName) => GameInfo;
  fetchPlayerGames: (rsoToken: string) => Promise<PlayerGameOutHttp[]>;
  isGamePlayable: (theme: ThemeName) => boolean;
};

const CashCodeContext = React.createContext<CashCodeState>({} as CashCodeState);

export const useCashCodeProvider = () => React.useContext(CashCodeContext);

export type CashCodeProviderProps = {
  children: React.ReactNode;
};

export const CashCodeProvider = ({ children }: CashCodeProviderProps) => {
  // Override RiotPay SDK base URLs if needed
  useEffect(() => {
    if (siteConfig.ENABLE_CASHCODES_API_BASE_OVERRIDE) {
      configureRiotPaySDK({
        baseUrls: {
          [ServiceName.LOCATION]: process.env.GATSBY_RIOTPAY_LOCATION_BASE,
          [ServiceName.RIOT_PAY_PMC]: process.env.GATSBY_RIOTPAY_PMC_BASE,
        },
      });
    }
  }, []);

  // Set up theme state with session storage
  const [theme, setTheme] = useSessionStorage<ThemeName>(siteConfig.KEY_REDEEM_THEME, "riot", true);

  // Set up games management
  const { getGameInfo, isGamePlayable, fetchPlayerGames, setGamesPlayed } = useGamesPlayed();

  // Set up cash code validation and redemption
  const { code, setCode, validationDetails, redemptionDetails, validateCode, redeemCode, resetCode } = useCashCode();

  // Reset all state
  const cancelRedeem = useCallback(() => {
    setTheme("riot");
    setGamesPlayed([]);
    resetCode();
  }, [setTheme, setGamesPlayed, resetCode]);

  const isCashCodeActive = useCallback(
    (providedCode?: string) => {
      // Use the provided code if available, otherwise fall back to the state's code
      const codeToCheck = providedCode || code;

      // If we don't have any code to check, return false
      if (!codeToCheck) {
        return false;
      }

      return siteConfig.ENABLE_CASH_CODES && isPrepaidCode(codeToCheck);
    },
    [code],
  );

  return (
    <CashCodeContext.Provider
      value={{
        isCashCodeActive,
        theme,
        setTheme,
        code,
        setCode,
        validationDetails,
        redemptionDetails,
        cancelRedeem,
        validateCode,
        redeemCode,
        getGameInfo,
        fetchPlayerGames,
        isGamePlayable,
      }}
    >
      {children}
    </CashCodeContext.Provider>
  );
};
